import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'wouter';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { Logo } from '@/components/ui/Logo';
import { BookCallButton } from '@/components/ui/BookCallButton';
import { site } from '@/content/site';
import { getLenis } from '@/lib/useLenis';
import { useLogoClick } from '@/lib/useScrollTop';
import { MQ } from '@/lib/motion';

gsap.registerPlugin(ScrollTrigger, useGSAP);

export function Nav() {
  const [location] = useLocation();
  const onLogoClick = useLogoClick();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Close the menu on navigation.
  useEffect(() => setOpen(false), [location]);

  // Lock scroll while the overlay is open, and close on Escape.
  useEffect(() => {
    const lenis = getLenis();
    if (open) {
      lenis?.stop();
      document.body.style.overflow = 'hidden';
    } else {
      lenis?.start();
      document.body.style.overflow = '';
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open]);

  /*
    Shrink and add a backdrop once scrolled past the hero's first fold.

    This is a plain scroll-position readout, NOT a trigger range. An earlier
    version drove it from ScrollTrigger's isActive, which reported false again
    once the page reached the bottom of the document — so the bar turned
    transparent over the dark CTA and footer and the white nav text lost its
    backing. "Scrolled" is simply scrollY > 24, at every position.
  */
  useGSAP(() => {
    const update = () => setScrolled(window.scrollY > 24);
    update();
    const st = ScrollTrigger.create({ start: 0, end: 'max', onUpdate: update, onRefresh: update });
    return () => st.kill();
  });

  // Stagger the overlay links in.
  useGSAP(
    () => {
      if (!open || !panelRef.current) return;
      const items = panelRef.current.querySelectorAll('[data-nav-item]');
      const mm = gsap.matchMedia();
      mm.add(MQ.motion, () => {
        gsap.fromTo(
          items,
          { opacity: 0, y: 24 },
          { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out', stagger: 0.06, delay: 0.08 },
        );
      });
      mm.add(MQ.reduced, () => gsap.set(items, { opacity: 1, y: 0 }));
      return () => mm.revert();
    },
    { dependencies: [open], scope: panelRef },
  );

  const onDark = !scrolled && !open;

  return (
    <>
      <header
        ref={ref}
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ease-brand ${
          open ? 'bg-white' : scrolled ? 'bg-white/85 shadow-sm backdrop-blur-xl' : 'bg-transparent'
        }`}
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div
          /* Matches the home hero's wider measure so the logo lines up with the
             headline beneath it; inner pages use .shell and still sit inside. */
          className={`mx-auto flex w-[min(96rem,100%-2rem)] items-center justify-between pl-[clamp(0rem,2vw,3.5rem)] transition-all duration-500 ease-brand md:w-[min(96rem,100%-4rem)] lg:w-[min(96rem,100%-5rem)] ${
            scrolled ? 'h-[76px]' : 'h-28'
          }`}
        >
          <Link
            href="/"
            onClick={onLogoClick}
            className={`logo-hover transition-colors duration-500 ${
              onDark ? 'text-white' : 'text-brand-mark'
            }`}
            aria-label={`${site.legalName} — home`}
          >
            {/*
              Full lockup only from lg up. Below that the "Private Limited"
              suffix would render under ~9px and become unreadable, so the mark
              stands alone rather than shrinking an illegible lockup.
            */}
            {/* Larger than it was: at 34px the lockup read as a footer mark
                rather than the anchor of the page. */}
            <span className="hidden lg:block">
              <Logo variant="lockup" size={scrolled ? 38 : 46} title="" />
            </span>
            <span className="block lg:hidden">
              <Logo variant="mark" size={34} title="" />
            </span>
          </Link>

          <nav className="hidden items-center gap-4 xl:gap-7 lg:flex" aria-label="Main">
            {site.nav.filter((item) => !['/', '/careers'].includes(item.href)).map((item) => {
              const active = location === item.href || location.startsWith(`${item.href}/`);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  aria-current={active ? 'page' : undefined}
                  /* Heavier and fully opaque: at text-sm/80% the links read as
                     secondary text rather than navigation. */
                  className={`group relative py-2 text-[0.95rem] font-semibold transition-colors duration-300 ${
                    onDark ? 'text-white/85 hover:text-white' : 'text-ink/75 hover:text-ink'
                  }`}
                >
                  {item.label}
                  <span
                    className={`absolute inset-x-0 -bottom-0.5 h-px origin-left bg-current transition-transform duration-500 ease-brand ${
                      active ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                    }`}
                  />
                </Link>
              );
            })}
            {/* Opens the booking page rather than routing to the form: the
                nav CTA is the highest-intent click on the site. */}
            <BookCallButton size="sm" variant="primary">
              Schedule a call
            </BookCallButton>
          </nav>

          {/* 44px minimum touch target. */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            className={`grid h-11 w-11 place-items-center rounded-full transition-colors duration-300 lg:hidden ${
              open || !onDark
                ? 'text-ink hover:bg-ink/8'
                : 'text-white hover:bg-white/12'
            }`}
          >
            <span className="relative block h-4 w-6">
              <span
                className={`absolute inset-x-0 h-0.5 bg-current transition-all duration-400 ease-brand ${
                  open ? 'top-1/2 rotate-45' : 'top-0'
                }`}
              />
              <span
                className={`absolute inset-x-0 top-1/2 h-0.5 bg-current transition-opacity duration-200 ${
                  open ? 'opacity-0' : 'opacity-100'
                }`}
              />
              <span
                className={`absolute inset-x-0 h-0.5 bg-current transition-all duration-400 ease-brand ${
                  open ? 'top-1/2 -rotate-45' : 'bottom-0'
                }`}
              />
            </span>
          </button>
        </div>
      </header>

      {/* Mobile overlay */}
      <div
        ref={panelRef}
        className={`fixed inset-0 z-40 overflow-y-auto bg-white lg:hidden ${open ? '' : 'pointer-events-none opacity-0'}`}
        style={{ transition: 'opacity 400ms var(--ease-brand)' }}
        aria-hidden={!open}
        {...(!open ? { inert: '' } : {})}
      >
        <div className="shell flex min-h-full flex-col pt-[calc(7rem+env(safe-area-inset-top))] pb-8">
          {site.nav.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              onClick={() => setOpen(false)}
              data-nav-item
              aria-current={location === item.href ? 'page' : undefined}
              className={`font-display flex min-h-12 items-center border-b border-hairline py-3 text-[1.375rem] font-semibold leading-snug sm:text-2xl ${location === item.href ? 'text-brand' : 'text-ink'}`}
            >
              {item.label}
            </Link>
          ))}
          {/* Closes the overlay on the way out: it locks body scroll, so
              restoring the page through browser history should not leave it locked. */}
          <div data-nav-item className="mt-6" onClick={() => setOpen(false)}>
            <BookCallButton size="lg" variant="primary" className="w-full">
              Schedule a call
            </BookCallButton>
          </div>
          <a
            data-nav-item
            href={`mailto:${site.email.general}`}
            // inline-flex + min-h-11 clears the 44px touch target; this sits in
            // the mobile overlay, so it is only ever tapped.
            className="mt-4 inline-flex min-h-11 items-center text-sm opacity-60"
          >
            {site.email.general}
          </a>
        </div>
      </div>
    </>
  );
}
